import { createSelector } from '@ngrx/store';
import { getRouterSelectors } from '@ngrx/router-store';

/**
 * Router Selectors
 * 
 * Provides memoized selectors for accessing router state from NgRx store.
 * These selectors replace manual router subscriptions in services.
 */

// Get NgRx router selectors
const {
  selectCurrentRoute,
  selectRouteParams,
  selectRouteData,
  selectUrl,
  selectQueryParams,
  selectRouteParam,
  selectQueryParam,
  selectFragment,
  selectTitle
} = getRouterSelectors();

// Re-export base router selectors
export {
  selectCurrentRoute,
  selectRouteParams,
  selectRouteData,
  selectUrl,
  selectQueryParams,
  selectRouteParam,
  selectQueryParam,
  selectFragment,
  selectTitle
};

/**
 * Public routes (unauthenticated pages)
 * These routes don't require authentication
 */
export const PUBLIC_ROUTES = [
  '/login',
  '/registration',
  '/forgot-password',
  '/confirm-email',
  '/confirm-reset-password',
  '/auth/callback',
  '/welcome'
] as const;

/**
 * Routes where avatar box should be full screen
 */
const FULLSCREEN_ROUTES = [
  '/home',
  '/'
] as const;

/**
 * Selects whether the current route is the home route
 */
export const selectIsHomeRoute = createSelector(
  selectUrl,
  (url) => url === '/' || url === '/home'
);

/**
 * Selects whether the current route is a public route (unauthenticated)
 * Used to determine if navigation to /home is needed after login
 */
export const selectIsPublicRoute = createSelector(
  selectUrl,
  (url) => url ? PUBLIC_ROUTES.some(route => url.startsWith(route)) : false
);

/**
 * Selects whether the current route should hide the avatar box
 * (Same as public routes)
 */
export const selectIsHiddenRoute = selectIsPublicRoute;

/**
 * Selects whether the current route should show avatar box in fullscreen
 */
export const selectIsFullscreenRoute = createSelector(
  selectUrl,
  (url) => url ? (FULLSCREEN_ROUTES as readonly string[]).includes(url) : false
);

/**
 * Selects whether the current route should show avatar box in minimized mode
 */
export const selectIsMinimizedRoute = createSelector(
  selectIsHiddenRoute,
  selectIsFullscreenRoute,
  (isHidden, isFullscreen) => !isHidden && !isFullscreen
);

/**
 * Determines the avatar box mode based on current route
 */
export const selectRouteBasedAvatarBoxMode = createSelector(
  selectIsHiddenRoute,
  selectIsFullscreenRoute,
  (isHidden, isFullscreen) => {
    if (isHidden) return 'hidden' as const;
    if (isFullscreen) return 'fullscreen' as const;
    return 'minimized' as const;
  }
);
